import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { safeSetItem } from '../utils/safeStorage';

const BrandingContext = createContext();

const DEFAULT_LOGO_URL = 'https://my.castingcalls.uk/storage/2025/03/ccuk-logo-shadow.png';
const DEFAULT_SITE_NAME = 'Casting Calls UK';
const DEFAULT_TAGLINE = '';

const DEFAULT_BRANDING = {
  logo_url: DEFAULT_LOGO_URL,
  logo_url_dark: null,
  favicon_url: null,
  site_name: DEFAULT_SITE_NAME,
  tagline: DEFAULT_TAGLINE,
  tagline_color: null,
  tagline_font_style: 'normal',
  tagline_font_weight: '500',
  footer_logo_url: null,
  footer_logo_url_dark: null,
  footer_description: "The UK's dedicated network for connecting talent, casting professionals, agents, and industry services.",
  footer_copyright: '© 2026 Casting Calls UK. All rights reserved.',

  // Splash / launch screen defaults
  splash_enabled: true,
  splash_bg_color: '#002FA7',
  splash_text_color: '#FFFFFF',
  splash_accent_color: '#6A8CFF',
  splash_text: 'Casting Calls UK',
  splash_logo_url: null,
};

const DEFAULT_PROMO = {
  enabled: false,
  message: '',
  link_url: null,
  button_label: null,
  bg_color: '#002FA7',
  text_color: '#FFFFFF',
  button_bg_color: '#FFFFFF',
  button_text_color: '#002FA7',
  glow: false,
  starts_at: null,
  ends_at: null,
};

function getInitialBranding() {
  try {
    const cached = localStorage.getItem('ccuk_branding');

    if (cached) {
      const parsed = JSON.parse(cached);
      return { ...DEFAULT_BRANDING, ...parsed };
    }
  } catch {
    // Ignore localStorage/private mode/cache errors.
  }

  return { ...DEFAULT_BRANDING };
}

function getInitialPromo() {
  try {
    const cached = localStorage.getItem('ccuk_promo_banner');

    if (cached) {
      return { ...DEFAULT_PROMO, ...JSON.parse(cached) };
    }
  } catch {
    // Ignore localStorage/private mode/cache errors.
  }

  return { ...DEFAULT_PROMO };
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(getInitialBranding);
  const [brandingLoaded, setBrandingLoaded] = useState(false);
  const [promoBanner, setPromoBanner] = useState(getInitialPromo);

  // Stable callbacks prevent PromoBanner's refresh interval from being torn
  // down and recreated whenever the provider re-renders.
  const refreshBranding = useCallback(async () => {
    try {
      const API_URL = window.__BACKEND_URL__;
      const response = await fetch(`${API_URL}/api/settings/branding`);

      if (response.ok) {
        const data = await response.json();
        const newBranding = { ...DEFAULT_BRANDING, ...data };

        if (!newBranding.logo_url) {
          newBranding.logo_url = DEFAULT_LOGO_URL;
        }

        setBranding(newBranding);

        // Never cache base64 data URLs. They can be huge and break
        // localStorage, especially in iOS Safari private browsing.
        const cacheable = { ...newBranding };

        [
          'logo_url',
          'logo_url_dark',
          'favicon_url',
          'footer_logo_url',
          'footer_logo_url_dark',
          'splash_logo_url',
        ].forEach((key) => {
          const value = cacheable[key];

          if (typeof value === 'string' && value.startsWith('data:')) {
            cacheable[key] = null;
          }
        });

        safeSetItem('ccuk_branding', JSON.stringify(cacheable));
      }
    } catch (error) {
      console.error('Failed to refresh branding:', error);
    } finally {
      setBrandingLoaded(true);
    }
  }, []);

  const refreshPromoBanner = useCallback(async () => {
    try {
      const API_URL = window.__BACKEND_URL__;
      const response = await fetch(`${API_URL}/api/settings/promo-banner`);

      if (response.ok) {
        const data = await response.json();
        const merged = { ...DEFAULT_PROMO, ...data };

        setPromoBanner(merged);
        safeSetItem('ccuk_promo_banner', JSON.stringify(merged));
      }
    } catch {
      // Silent fail — the promotion banner is non-critical.
    }
  }, []);

  useEffect(() => {
    refreshBranding();
    refreshPromoBanner();
  }, [refreshBranding, refreshPromoBanner]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const faviconUrl = branding?.favicon_url;
    if (!faviconUrl) return;

    let favicon = document.querySelector('link[data-dynamic-favicon]');

    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.setAttribute('data-dynamic-favicon', '');
      document.head.appendChild(favicon);
    }

    favicon.href = faviconUrl;

    let appleIcon = document.querySelector('link[data-dynamic-apple-icon]');

    if (!appleIcon) {
      appleIcon = document.createElement('link');
      appleIcon.rel = 'apple-touch-icon';
      appleIcon.setAttribute('data-dynamic-apple-icon', '');
      document.head.appendChild(appleIcon);
    }

    appleIcon.href = faviconUrl;
  }, [branding?.favicon_url]);

  return (
    <BrandingContext.Provider
      value={{
        branding,
        brandingLoaded,
        refreshBranding,
        promoBanner,
        refreshPromoBanner,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);

  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }

  return context;
}
